import { Tooltip } from '@material-ui/core';
import { iconInfor } from 'assets/icons';
import React, { useState, forwardRef, useEffect } from 'react';
import useStyles from './styles';
import { useDispatch } from 'react-redux';
import { alertActions } from 'actions/alert.actions';
import _ from 'lodash';

interface IUploadMediaProps {
  label: string;
  tooltipComponent?: NonNullable<React.ReactNode>;
  customClass?: string;
  placeholder?: string | React.ReactNode;
  acceptType?: string;
  name?: string;
  onChangeImage?: (image: any, thumbnail: any) => void;
  mediaUrl?: string | any;
  errors?: boolean;
  disable?: boolean;
  onlyAcceptType?: string[];
  disableDelete?: any;
  thumbNail?: any;
  customUpload?: any;
  cypressImageUpload?: string;
}

const UploadMedia = forwardRef((props: IUploadMediaProps, ref: any) => {
  const styles = useStyles();
  const {
    label,
    tooltipComponent,
    customClass,
    placeholder,
    acceptType,
    name,
    onChangeImage,
    mediaUrl,
    errors,
    disable,
    onlyAcceptType,
    disableDelete,
    customUpload,
    cypressImageUpload,
  } = props;

  const [currentImage, setCurrentImage] = useState<any>(null);
  const [currentVideo, setCurrentVideo] = useState<any>(null);
  const dispatch = useDispatch()

  useEffect(() => {
    const checkIfVideo = (text: string) => {
      return text?.includes("video") 
        || text?.includes("mp4")
        || text?.includes("application/octet-stream") 
        || text?.includes("mov")
    }

    const checkIfImage = (text: string) => {
      return text?.includes("jpg") 
      || text?.includes("gif") 
      || text?.includes("jpeg")
      || text?.includes("png")
    }

    if (mediaUrl) {
      try {
        let url = mediaUrl;
        if (!url?.includes("http")) {
          const mediaURlSplited = mediaUrl.split(";");
          url = mediaURlSplited[0];
        } 
        if (checkIfImage(url)) {
          setCurrentVideo(null);
          setCurrentImage(mediaUrl);
        }else  if (checkIfVideo(url)) {
          setCurrentImage(null);
          setCurrentVideo(mediaUrl);
        }
      } catch(err) {
        console.log(err);
      }
    } else {
      setCurrentImage(null);
      setCurrentVideo(null);
    }
  }, [mediaUrl]);

  const snapImage = (video: any, url: any) => {
    const canvas = document.createElement("canvas");
    canvas.width = 100;
    canvas.height = 100;
    const context: any = canvas.getContext("2d");
    context.globalAlpha = 1;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL();
    return { image };
  }

  function handleChange(event: any) {
    const file = event.target.files[0];
    if (file?.size > 10 * 1024 * 1024) {
      dispatch(alertActions.error('Size of image is over 10mb! Please try again.'))
      return;
    }
    const fileReader = new FileReader();
    if (file?.name?.includes("mp4") || file?.name?.includes("mov")) {
      fileReader.onload = function () {
        const blob = new Blob([fileReader.result], { type: file.type });
        const url = URL.createObjectURL(blob);
        const video = document.createElement("video");
  
        const timeupdate = function () {
          const snapObj = snapImage(video, url);
          
          video.removeEventListener("timeupdate", timeupdate);
          video.pause();
          setCurrentImage(snapObj?.image);
          onChangeImage && onChangeImage(file, snapObj?.image);
        };
  
        video.addEventListener("loadeddata", function () {
          const snapObj = snapImage(video, url);
          video.removeEventListener("timeupdate", timeupdate);
          setCurrentImage(snapObj?.image);
          onChangeImage && onChangeImage(file, snapObj?.image);
        });

        video.addEventListener("timeupdate", timeupdate);
        video.preload = "metadata";
        video.src = url;
        video.muted = true;
        video.playsInline = true;
        video.play();
      };
  
      fileReader.readAsArrayBuffer(file);
    } else {
      const imageUrl = window.URL.createObjectURL(file);
      onChangeImage && onChangeImage(file, null);
      setCurrentImage(imageUrl);
    }
  }

  const handleRemoveImg = () => {
    onChangeImage && onChangeImage('', null);
    if(disableDelete) return;
    setCurrentImage(null);
    setCurrentVideo(null)
  };

  const getAcceptedType = () => {
    if (onlyAcceptType && onlyAcceptType?.length > 0) {
      return onlyAcceptType.join(" ");
    }
    return `${acceptType || ''} image/png, image/jpg, image/jpeg, image/tiff`
  }

  return (
    <div className={`${customClass} ${errors && styles.containerErr}`}>
      <div className={styles.viewLabel}>
        <label className={styles.label} id='label-title'>
          {label}
        </label>
        {tooltipComponent ? (
          <Tooltip
            placement='top'
            title={tooltipComponent}
            classes={{
              tooltip: styles.tooltip,
            }}
          >
            <img src={iconInfor} />
          </Tooltip>
        ) : null}
      </div>
      {currentImage || currentVideo ? (
        <div className={`${styles.viewImage} ${disable && styles.disable}`}>

          { currentVideo ? 
            <video className={styles.imgUploaded} autoPlay loop muted>
               <source src={currentVideo} type="video/mp4"></source>
            </video>
            : <img src={currentImage} className={styles.imgUploaded} />}
    
          <span
            className={`${styles.buttonRemove} style-button-remove`}
            onClick={disable ? () => { } : handleRemoveImg}
            data-cy="cypress-course=plan-remove-image"
          >
            x
          </span>
        </div>
      ) : (
        <div className={`${customUpload ? customUpload : styles.uploadImage} uploadImage ${customUpload}`} style={{zIndex: 999}}>
          {
            React.isValidElement(props?.placeholder) ? placeholder : 
            <label className={styles.uploadImageLabel} id='label-upload'>
              {placeholder ? placeholder : 'Upload Image'}
            </label>
          }
          <input
            ref={ref}
            type='file'
            name={name}
            onChange={(e: any) => handleChange(e)}
            accept={getAcceptedType()}
            disabled={disable}
            style={{cursor: 'pointer'}}
            data-cy={cypressImageUpload}
          />
        </div>
      )}
    </div>
  );
});

export default UploadMedia;
